import { Params } from '@angular/router';

import { CONTAIN_FILTER_SEPARATOR } from 'src/app/@shared/base-list-filter-page';
import { FormConfig, FormFieldConfig, FormFieldValue } from 'src/app/@shared/form/form.types';

function isBooleanSelectField(fieldConfig?: FormFieldConfig): boolean {
  if (!fieldConfig || fieldConfig.type !== 'select') {
    return false;
  }

  const options = fieldConfig.options || [];

  return fieldConfig.optionsCategory === 'boolean'
    && options.length === 2
    && options[0] === 'true'
    && options[1] === 'false';
}

export function normalizeFilterValueByConfig(value: unknown, fieldConfig?: FormFieldConfig): FormFieldValue | FormFieldValue[] | null {
  if (!fieldConfig) {
    return value as FormFieldValue;
  }

  if (!isBooleanSelectField(fieldConfig)) {
    return value as FormFieldValue;
  }

  if (value === true || value === 'true') {
    return true;
  }

  if (value === false || value === 'false') {
    return false;
  }

  return null;
}

export function normalizeFilterResultByConfig<T extends Record<string, unknown>>(rawFilterResult: T, formConfig: FormConfig): Partial<T> {
  if (!rawFilterResult) {
    return {};
  }

  return Object.entries(rawFilterResult).reduce((result, [key, rawValue]) => {
    const normalized = normalizeFilterValueByConfig(rawValue, formConfig[key]);

    if (normalized === null) {
      return result;
    }

    result[key as keyof T] = normalized as T[keyof T];

    return result;
  }, {} as Partial<T>);
}

export function getControlValueByConfig(value: unknown, fieldConfig?: FormFieldConfig): FormFieldValue | FormFieldValue[] | null {
  if (!fieldConfig) {
    return value as FormFieldValue;
  }

  if (!isBooleanSelectField(fieldConfig)) {
    return value as FormFieldValue;
  }

  if (value === true || value === 'true') {
    return 'true';
  }

  if (value === false || value === 'false') {
    return 'false';
  }

  return null;
}

export function getFilterResultFromQueryParamsByConfig<T extends Record<string, unknown>>(queryParams: Params,
  formConfig: FormConfig, filterKeys: string[], arrayKeys: string[] = []): Partial<T> {
  const result: Partial<T> = {};

  for (const key of filterKeys) {
    const rawValue = queryParams[key];

    if (!rawValue && rawValue !== false) {
      continue;
    }

    if (arrayKeys.includes(key)) {
      const values = Array.isArray(rawValue)
        ? rawValue.map((value) => {
          return value.toString();
        })
        : rawValue.toString()
          .split(CONTAIN_FILTER_SEPARATOR)
          .filter(Boolean);

      result[key as keyof T] = values as T[keyof T];
      continue;
    }

    const normalized = normalizeFilterValueByConfig(rawValue, formConfig[key]);

    if (normalized !== null) {
      result[key as keyof T] = normalized as T[keyof T];
      continue;
    }

    if (rawValue !== null && typeof rawValue !== 'undefined') {
      result[key as keyof T] = rawValue as T[keyof T];
    }
  }

  return result;
}
